// Offline Queue Manager - IndexedDB-based event queue for Forward Command
// Stores events and blobs locally for sync when back online

const DB_NAME = "forward-command-offline";
const DB_VERSION = 1;
const EVENTS_STORE = "events";
const BLOBS_STORE = "blobs";

let db = null;

/**
 * Initialize IndexedDB
 * @returns {Promise<IDBDatabase>}
 */
export async function initDB() {
  if (db) return db;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error("Failed to open IndexedDB:", request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Events store - queued actions
      if (!database.objectStoreNames.contains(EVENTS_STORE)) {
        const eventsStore = database.createObjectStore(EVENTS_STORE, {
          keyPath: "id",
        });
        eventsStore.createIndex("status", "status", { unique: false });
        eventsStore.createIndex("createdAt", "createdAt", { unique: false });
        eventsStore.createIndex("type", "type", { unique: false });
      }

      // Blobs store - binary data (photos, audio)
      if (!database.objectStoreNames.contains(BLOBS_STORE)) {
        database.createObjectStore(BLOBS_STORE, { keyPath: "key" });
      }
    };
  });
}

/**
 * Generate a unique ID for events
 * @returns {string}
 */
function generateId() {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Event types supported by the queue
 */
export const EventTypes = {
  PHOTO_ADD: "PHOTO_ADD",
  VOICE_ADD: "VOICE_ADD",
  STATUS_ADD: "STATUS_ADD",
  TIME_ALLOCATION_ADD: "TIME_ALLOCATION_ADD",
  CLOCK_IN: "CLOCK_IN",
  CLOCK_OUT: "CLOCK_OUT",
  PRODUCTION_ADD: "PRODUCTION_ADD",
};

/**
 * Queue an event for later sync
 * @param {string} type - Event type from EventTypes
 * @param {object} payload - Data to save
 * @param {object} metadata - Context info (jobId, userId, etc.)
 * @param {Blob} [blob] - Optional binary data (photo/audio)
 * @returns {Promise<object>} The created event
 */
export async function queueEvent(type, payload, metadata, blob = null) {
  await initDB();

  const eventId = generateId();
  let blobKey = null;

  // Store blob separately if provided
  if (blob) {
    blobKey = `blob_${eventId}`;
    await storeBlob(blobKey, blob);
  }

  const event = {
    id: eventId,
    type,
    payload,
    blobKey,
    metadata: {
      ...metadata,
      timestamp: new Date().toISOString(),
    },
    status: "pending",
    retryCount: 0,
    createdAt: new Date().toISOString(),
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([EVENTS_STORE], "readwrite");
    const store = transaction.objectStore(EVENTS_STORE);
    const request = store.add(event);

    request.onsuccess = () => {
      resolve(event);
    };

    request.onerror = () => {
      console.error("Failed to queue event:", request.error);
      reject(request.error);
    };
  });
}

/**
 * Get all queued events
 * @param {string} [status] - Optional filter by status
 * @returns {Promise<Array>}
 */
export async function getQueuedEvents(status = null) {
  await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([EVENTS_STORE], "readonly");
    const store = transaction.objectStore(EVENTS_STORE);

    let request;
    if (status) {
      const index = store.index("status");
      request = index.getAll(status);
    } else {
      request = store.getAll();
    }

    request.onsuccess = () => {
      // Sort by createdAt
      const events = request.result.sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      );
      resolve(events);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

/**
 * Get pending events count (for badge display)
 * @returns {Promise<number>}
 */
export async function getPendingCount() {
  const events = await getQueuedEvents("pending");
  return events.length;
}

/**
 * Get pending counts by event type
 * @returns {Promise<object>} Counts keyed by event type
 */
export async function getPendingCountsByType() {
  const events = await getQueuedEvents("pending");
  const counts = {};

  for (const event of events) {
    counts[event.type] = (counts[event.type] || 0) + 1;
  }

  return counts;
}

/**
 * Update event status
 * @param {string} id - Event ID
 * @param {string} status - New status
 * @returns {Promise<void>}
 */
export async function updateEventStatus(id, status) {
  await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([EVENTS_STORE], "readwrite");
    const store = transaction.objectStore(EVENTS_STORE);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const event = getRequest.result;
      if (!event) {
        reject(new Error(`Event not found: ${id}`));
        return;
      }

      event.status = status;
      const putRequest = store.put(event);

      putRequest.onsuccess = () => resolve();
      putRequest.onerror = () => reject(putRequest.error);
    };

    getRequest.onerror = () => reject(getRequest.error);
  });
}

/**
 * Mark event as synced and remove it
 * @param {string} id - Event ID
 * @returns {Promise<void>}
 */
export async function markSynced(id) {
  await initDB();

  // Get the event first to check for blob
  const event = await getEvent(id);

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(
      [EVENTS_STORE, BLOBS_STORE],
      "readwrite",
    );

    // Delete event
    const eventsStore = transaction.objectStore(EVENTS_STORE);
    eventsStore.delete(id);

    // Delete associated blob if exists
    if (event?.blobKey) {
      const blobsStore = transaction.objectStore(BLOBS_STORE);
      blobsStore.delete(event.blobKey);
    }

    transaction.oncomplete = () => {
      resolve();
    };

    transaction.onerror = () => {
      reject(transaction.error);
    };
  });
}

/**
 * Mark event as failed and increment retry count
 * @param {string} id - Event ID
 * @returns {Promise<object>} Updated event
 */
export async function markFailed(id) {
  await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([EVENTS_STORE], "readwrite");
    const store = transaction.objectStore(EVENTS_STORE);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const event = getRequest.result;
      if (!event) {
        reject(new Error(`Event not found: ${id}`));
        return;
      }

      event.status = "failed";
      event.retryCount = (event.retryCount || 0) + 1;
      event.lastError = new Date().toISOString();

      const putRequest = store.put(event);

      putRequest.onsuccess = () => resolve(event);
      putRequest.onerror = () => reject(putRequest.error);
    };

    getRequest.onerror = () => reject(getRequest.error);
  });
}

/**
 * Get a single event by ID
 * @param {string} id - Event ID
 * @returns {Promise<object|null>}
 */
export async function getEvent(id) {
  await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([EVENTS_STORE], "readonly");
    const store = transaction.objectStore(EVENTS_STORE);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Store a blob (photo/audio) in IndexedDB
 * @param {string} key - Unique key for the blob
 * @param {Blob} blob - Binary data
 * @returns {Promise<void>}
 */
export async function storeBlob(key, blob) {
  await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([BLOBS_STORE], "readwrite");
    const store = transaction.objectStore(BLOBS_STORE);
    const request = store.put({
      key,
      blob,
      storedAt: new Date().toISOString(),
    });

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      console.error("Failed to store blob:", request.error);
      reject(request.error);
    };
  });
}

/**
 * Retrieve a blob from IndexedDB
 * @param {string} key - Blob key
 * @returns {Promise<Blob|null>}
 */
export async function getBlob(key) {
  await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([BLOBS_STORE], "readonly");
    const store = transaction.objectStore(BLOBS_STORE);
    const request = store.get(key);

    request.onsuccess = () => {
      const result = request.result;
      resolve(result?.blob || null);
    };

    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete a blob from IndexedDB
 * @param {string} key - Blob key
 * @returns {Promise<void>}
 */
export async function deleteBlob(key) {
  await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([BLOBS_STORE], "readwrite");
    const store = transaction.objectStore(BLOBS_STORE);
    const request = store.delete(key);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Clear all queued events and blobs (use with caution)
 * @returns {Promise<void>}
 */
export async function clearAll() {
  await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(
      [EVENTS_STORE, BLOBS_STORE],
      "readwrite",
    );

    transaction.objectStore(EVENTS_STORE).clear();
    transaction.objectStore(BLOBS_STORE).clear();

    transaction.oncomplete = () => {
      resolve();
    };

    transaction.onerror = () => reject(transaction.error);
  });
}

/**
 * Get total storage used by blobs (approximate)
 * @returns {Promise<number>} Size in bytes
 */
export async function getBlobStorageSize() {
  await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([BLOBS_STORE], "readonly");
    const store = transaction.objectStore(BLOBS_STORE);
    const request = store.getAll();

    request.onsuccess = () => {
      const total = request.result.reduce((sum, item) => {
        return sum + (item.blob?.size || 0);
      }, 0);
      resolve(total);
    };

    request.onerror = () => reject(request.error);
  });
}

export default {
  initDB,
  EventTypes,
  queueEvent,
  getQueuedEvents,
  getPendingCount,
  getPendingCountsByType,
  getEvent,
  updateEventStatus,
  markSynced,
  markFailed,
  storeBlob,
  getBlob,
  deleteBlob,
  clearAll,
  getBlobStorageSize,
};
